/* =========================================================
   Smooth scroll — ScrollSmoother a nivel de theme

   Monta ScrollSmoother sobre #smooth-wrapper > #smooth-content (ver
   layout/theme.liquid) y con ello activa dos cosas:

   1. Scroll con inercia en escritorio.
   2. El sistema de "effects": los elementos con data-speed se mueven a un
      ritmo distinto al del scroll. Eso es lo que hace funcionar el parallax
      de brand-teaser.liquid. Sin este archivo, data-speed es un atributo
      inerte y las secciones siguen funcionando sin parallax.

   Este archivo define window.dermxGsapReady, la misma promesa que usan
   brand-teaser.js y split-reveal.js. Al cargarse en el <head> con defer se
   ejecuta antes que los scripts de las secciones, así que cuando estas creen
   sus ScrollTriggers el smoother ya existe y los adopta con el scroller
   correcto desde el principio.

   Qué NO va dentro del contenido suavizado: cualquier position: fixed. El
   #smooth-content lleva un transform, y un transform crea containing block
   para los descendientes fixed, que dejarían de referirse al viewport. En
   este theme eso solo afecta al cart drawer, que se queda fuera del wrapper.
   Los drawers del header (menú, buscador) usan position: absolute y por eso
   sí pueden ir dentro.
   ========================================================= */

(function () {
  'use strict';

  if (window.DermxSmoothScroll) return;

  var GSAP_VERSION = '3.13.0';
  var CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/';

  /* Segundos que tarda el contenido en alcanzar la posición real del scroll.
     Es el dial de "peso" del scroll. Ojo al subirlo: los ScrollTrigger se
     disparan según el scroll nativo, no según lo que se ve, así que cuanto
     más alto, más se adelantan visualmente los reveals. */
  var SMOOTH = 0.8;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------
     Carga de scripts
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> plugins
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  function globalPlugin(name) {
    return window[name] || (window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* ---------------------------------------------------------
     Promesa compartida con las secciones

     Resuelve { gsap, ScrollTrigger, ScrollSmoother, smoother }. Las secciones
     solo leen gsap y ScrollTrigger, así que el objeto es compatible con el
     que construyen ellas por su cuenta si este archivo no está.
     --------------------------------------------------------- */

  window.dermxGsapReady = Promise.resolve()
    .then(function () {
      if (!window.gsap) return loadScript(CDN + 'gsap.min.js');
      return null;
    })
    .then(function () {
      if (!window.gsap) throw new Error('[smooth-scroll] GSAP no disponible');
      if (!globalPlugin('ScrollTrigger')) return loadScript(CDN + 'ScrollTrigger.min.js');
      return null;
    })
    .then(function () {
      // ScrollSmoother solo hace falta si vamos a suavizar de verdad.
      if (prefersReducedMotion.matches) return null;
      if (!globalPlugin('ScrollSmoother')) return loadScript(CDN + 'ScrollSmoother.min.js');
      return null;
    })
    .then(function () {
      var gsap = window.gsap;
      var ScrollTrigger = globalPlugin('ScrollTrigger');

      if (!ScrollTrigger) throw new Error('[smooth-scroll] ScrollTrigger no disponible');
      if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

      var ScrollSmoother = globalPlugin('ScrollSmoother');
      if (ScrollSmoother && !isPluginRegistered('ScrollSmoother')) {
        gsap.registerPlugin(ScrollSmoother);
      }

      // En móvil, mostrar/ocultar la barra de direcciones cambia innerHeight:
      // sin esto ScrollTrigger recalcularía a media animación y daría saltos.
      ScrollTrigger.config({ ignoreMobileResize: true });

      return {
        gsap: gsap,
        ScrollTrigger: ScrollTrigger,
        ScrollSmoother: ScrollSmoother || null,
        smoother: createSmoother(gsap, ScrollTrigger, ScrollSmoother),
      };
    })
    .catch(function (error) {
      // Sin GSAP no hay scroll suave ni parallax, pero el scroll nativo sigue
      // funcionando: #smooth-wrapper y #smooth-content son divs normales
      // mientras nadie los toque.
      console.warn(error);
      throw error;
    });

  /* Las secciones que consumen la promesa ya tienen su propio catch (pintan su
     estado final). Este handler vacío existe solo para que, en una plantilla
     sin ninguna sección que la consuma, un fallo del CDN no salga como
     "unhandled promise rejection" en consola. */
  window.dermxGsapReady.catch(function () {});

  /* ---------------------------------------------------------
     Creación del smoother
     --------------------------------------------------------- */

  function createSmoother(gsap, ScrollTrigger, ScrollSmoother) {
    if (!ScrollSmoother) return null; // movimiento reducido, o no cargó
    if (!document.getElementById('smooth-content')) return null;

    var existing = ScrollSmoother.get();
    if (existing) return existing;

    var smoother = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: SMOOTH,
      /* 0 = sin inercia en táctil. El scroll nativo del móvil ya tiene su
         propia física y suavizarlo encima se siente pastoso, además de
         romper el "scroll to top" del sistema. Con smoothTouch a 0 el
         wrapper se queda en flujo normal (sin position: fixed) pero los
         effects, es decir el parallax, siguen aplicándose. */
      smoothTouch: 0,
      effects: true,
    });

    /* Con inercia, ScrollSmoother deja el wrapper en position: fixed con
       overflow hidden y traslada el contenido. Esa es exactamente la condición
       que rompe el position: sticky de Dawn, así que la comprobamos tal cual en
       vez de deducirla de la config: en táctil (smoothTouch 0) el wrapper se
       queda en flujo, el transform se limpia y el sticky nativo funciona solo.
       La clase queda expuesta por si hace falta enganchar CSS a este estado. */
    var wrapper = document.getElementById('smooth-wrapper');
    var isTransformed = wrapper && window.getComputedStyle(wrapper).position === 'fixed';

    if (isTransformed) {
      document.documentElement.classList.add('smooth-scroll-active');
      pinHeader(ScrollTrigger);
    }

    return smoother;
  }

  /* ---------------------------------------------------------
     Header sticky

     Dentro del contenido suavizado, el position: sticky de Dawn no funciona:
     su scrollport pasa a ser #smooth-wrapper, que tiene overflow hidden y
     nunca se desplaza, así que el header se va con el contenido. Lo pineamos
     con ScrollTrigger, que sí está sincronizado con el smoother.

     pinSpacing: false porque el header debe solaparse con el contenido al
     hacer scroll, no empujarlo. El pin arranca en 'top top', o sea cuando el
     header llega arriba: si hay barra de anuncios encima, esta se va con el
     scroll y el header se queda, que es justo el comportamiento "Siempre"
     de Dawn.
     --------------------------------------------------------- */

  var headerPin = null;

  function pinHeader(ScrollTrigger) {
    if (headerPin) {
      headerPin.kill(true);
      headerPin = null;
    }

    // Dawn solo renderiza <sticky-header> si el ajuste no es "Ninguno".
    if (!document.querySelector('sticky-header')) return;

    var header = document.querySelector('.section-header');
    if (!header) return;

    /* Si el usuario ya empezó a hacer scroll mientras GSAP se descargaba del
       CDN, el hide/reveal de Dawn habrá dejado puestas sus clases de oculto.
       Pinear el header en ese estado congela ese desplazamiento y es lo que
       hace que a veces se quede a medio subir, así que lo devolvemos a reposo
       antes de medirlo. */
    header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');

    headerPin = ScrollTrigger.create({
      id: 'smooth-scroll-header',
      trigger: header,
      start: 'top top',
      end: 'max',
      pin: true,
      pinSpacing: false,
    });
  }

  /* ---------------------------------------------------------
     Utilidades públicas + Theme Editor
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      var ScrollTrigger = globalPlugin('ScrollTrigger');
      if (ScrollTrigger) ScrollTrigger.refresh();
    }, 60);
  }

  window.DermxSmoothScroll = {
    ready: window.dermxGsapReady,
    get: function () {
      var ScrollSmoother = globalPlugin('ScrollSmoother');
      return (ScrollSmoother && ScrollSmoother.get()) || null;
    },
    refreshSoon: refreshSoon,
  };

  /* El contenido cambia de alto al re-renderizar una sección en el editor. */
  document.addEventListener('shopify:section:load', function (event) {
    var ScrollTrigger = globalPlugin('ScrollTrigger');
    if (!ScrollTrigger) return;

    // Si la sección recargada es el header, su pin apuntaba a un nodo muerto.
    if (event.target.querySelector('sticky-header, .section-header')) {
      if (document.documentElement.classList.contains('smooth-scroll-active')) {
        pinHeader(ScrollTrigger);
      }
    }

    refreshSoon();
  });

  document.addEventListener('shopify:section:unload', refreshSoon);

  /* Las imágenes que cargan después mueven todo lo que hay debajo. */
  window.addEventListener('load', refreshSoon);
})();
